/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormLabel,
  EuiFormHelpText,
  EuiSpacer,
  EuiFieldText,
  EuiFormRow,
} from '@elastic/eui'

import type { User } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, StackDeployment, FoundUser } from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { createNewShieldUser } from '@/lib/stackDeployments/credentials'
import { replaceIn } from '@/lib/immutability-helpers'
import { createTempShieldUserSuccessAction } from '@/actions/stackDeployments/metadata'

import SpinButton from '../../SpinButton'

import TempUserCreationSuccess from './TempUserCreationSuccess'

import type { Action } from '@/types/redux'

export type Props = {
  newTempShieldUser: FoundUser | null
  user: Pick<User, 'user_name'> | null
  deployment: StackDeployment
  updateMetadata: (params: {
    deployment: StackDeployment
    updater: (metadata: { [key: string]: any }) => { [key: string]: any }
    requestMeta?: { [key: string]: any }
    then?: () => Action<string>
  }) => void
  setDeploymentResourceMetadataRequest: AsyncRequestState
}

type State = {
  _user: boolean
  username: string
}

const maxUsernameLength = 15
const createUserMetadataAction = `create-user`

export const sanitizeUsername = (username: string): string =>
  username.replace(/@.*/, '').replace(/\.+/g, '-').substring(0, maxUsernameLength)

export const validateUsername = (username: string): boolean => /^[A-Za-z0-9-]+$/.test(username)

export default class AddTempUser extends Component<Props, State> {
  state = {
    _user: Boolean(this.props.user),
    username: this.props.user ? sanitizeUsername(this.props.user.user_name) : ``,
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State): Partial<State> | null {
    if (!prevState._user && nextProps.user) {
      return {
        _user: true,
        username: sanitizeUsername(nextProps.user.user_name),
      }
    }

    return null
  }

  render() {
    const { setDeploymentResourceMetadataRequest, newTempShieldUser, deployment } = this.props
    const { username } = this.state
    const updateRequestIsRelevant =
      setDeploymentResourceMetadataRequest.meta.action === createUserMetadataAction

    const isUsernameEmpty = username === ''
    const invalidUsername = !isUsernameEmpty && !validateUsername(username)

    return (
      <div>
        <EuiSpacer size='m' />

        <EuiFormLabel>
          <FormattedMessage
            id='cluster-manage-add-temp-user.add-a-new-temporary-shield-user-by-adding-a-username'
            defaultMessage='Create temporary shield users'
          />
        </EuiFormLabel>

        <EuiSpacer size='xs' />

        <EuiFlexGroup gutterSize='s'>
          <EuiFlexItem grow={false}>
            <EuiFormRow
              isInvalid={invalidUsername}
              error={
                <FormattedMessage
                  id='cluster-manage-add-temp-user.invalid-username'
                  defaultMessage='Usernames can only contain letters, digits or dashes.'
                />
              }
            >
              <EuiFieldText
                data-test-subj='add-temp-user-username'
                value={username}
                onChange={(e) => this.updateUsername(e.target.value)}
                maxLength={maxUsernameLength}
                className='tempShieldUsers-input'
              />
            </EuiFormRow>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <div>
              <PermissionsGate
                permissions={[
                  {
                    type: 'deployment',
                    action: 'update',
                    id: deployment.id,
                  },
                ]}
              >
                {({ hasPermissions }) => (
                  <SpinButton
                    data-test-id='add-temp-user-submit'
                    color='primary'
                    className='tempShieldUsers-btn'
                    onClick={() => this.createUser()}
                    disabled={
                      username === `` ||
                      (!updateRequestIsRelevant &&
                        setDeploymentResourceMetadataRequest.inProgress) ||
                      isUsernameEmpty ||
                      invalidUsername ||
                      !hasPermissions
                    }
                    spin={
                      updateRequestIsRelevant && setDeploymentResourceMetadataRequest.inProgress
                    }
                  >
                    <FormattedMessage
                      id='cluster-manage-add-temp-user.add-new-user'
                      defaultMessage='Create'
                    />
                  </SpinButton>
                )}
              </PermissionsGate>

              <EuiFormHelpText>
                <FormattedMessage
                  id='cluster-manage-add-temp-user.new-username-explained'
                  defaultMessage='Creates a new user named { username }'
                  values={{ username: `cloud-internal-${username}` }}
                />
              </EuiFormHelpText>
            </div>
          </EuiFlexItem>
        </EuiFlexGroup>

        {updateRequestIsRelevant && setDeploymentResourceMetadataRequest.error && (
          <Fragment>
            <EuiSpacer size='m' />

            <CuiAlert type='error'>{setDeploymentResourceMetadataRequest.error}</CuiAlert>
          </Fragment>
        )}

        {!setDeploymentResourceMetadataRequest.inProgress &&
          !setDeploymentResourceMetadataRequest.error &&
          newTempShieldUser !== null && (
            <Fragment>
              <EuiSpacer size='m' />

              <TempUserCreationSuccess newTempShieldUser={newTempShieldUser} />
            </Fragment>
          )}
      </div>
    )
  }

  createUser(): void {
    const { username } = this.state
    const { deployment, updateMetadata } = this.props

    const newUser = createNewShieldUser({ username })
    const then = () => createTempShieldUserSuccessAction(newUser)

    updateMetadata({
      deployment,
      then,
      requestMeta: { action: createUserMetadataAction, username: newUser.username },
      updater: (metadata) =>
        replaceIn(metadata, [`shield`, `found_users`, newUser.username], newUser),
    })
  }

  updateUsername(username: string): void {
    this.setState({
      username,
    })
  }
}
